import { useEffect, useState } from "react";
import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getFilteredData } from "../../../utils/functions/filteredForms";

function FiltersModal({ t, filters, completions, onApply, onClose }) {
  const [selectedValues, setSelectedValues] = useState({});

  useEffect(() => {
    setSelectedValues(
      Object.fromEntries(
        filters.map((filter) => [
          filter.id,
          filter.options.find((option) => option.actif)?.value ?? "",
        ]),
      ),
    );
  }, [filters]);

  function handleApply() {
    onApply(
      filters.map((filter) => ({
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          actif:
            selectedValues[filter.id] !== "" &&
            option.value === selectedValues[filter.id],
        })),
      })),
    );
  }

  function getMatchingCompletionCount(filterId, optionValue) {
    const candidateFilters = filters.map((filter) => ({
      ...filter,
      options: filter.options.map((option) => ({
        ...option,
        actif:
          (filter.id === filterId
            ? option.value === optionValue
            : option.value === selectedValues[filter.id]) && optionValue !== "",
      })),
    }));

    return getFilteredData(candidateFilters, completions).length;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center bg-black/45 items-center p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-2xl bg-white p-5 shadow-2xl rounded-[11px] sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filters-modal-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-perso">
              <AdjustmentsHorizontalIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 id="filters-modal-title" className="text-lg font-bold text-gray-900">
                {t("Filters")}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {t("Filtrer les programmes affichés")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label={t("Close")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[30vh] space-y-5 overflow-y-auto pr-1 border-b border-t pt-2 pb-2">
          {filters.map((filter) => (
            <fieldset key={filter.id} className="min-w-0">
              <legend className="mb-2 block text-sm font-semibold text-gray-800">
                {filter.label}
              </legend>
              <div className="flex flex-wrap gap-2" role="radiogroup">
                <button
                  type="button"
                  role="radio"
                  aria-checked={(selectedValues[filter.id] ?? "") === ""}
                  onClick={() =>
                    setSelectedValues((currentValues) => ({
                      ...currentValues,
                      [filter.id]: "",
                    }))
                  }
                  className={`min-h-11 rounded-[8px] border px-3 py-2 text-sm font-medium transition-colors ${
                    (selectedValues[filter.id] ?? "") === ""
                      ? "border-primary-perso bg-primary-perso text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t("All")}
                </button>
                {filter.options.map((option) => {
                  const isSelected = selectedValues[filter.id] === option.value;
                  const matchingCompletionCount = getMatchingCompletionCount(
                    filter.id,
                    option.value,
                  );
                  const isAvailable = matchingCompletionCount > 0;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={!isSelected && !isAvailable}
                      onClick={() =>
                        setSelectedValues((currentValues) => ({
                          ...currentValues,
                          [filter.id]: option.value,
                        }))
                      }
                      className={`min-h-11 rounded-[8px] border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-300 ${
                        isSelected
                          ? "border-primary-perso bg-primary-perso text-white"
                          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="flex gap-3 pt-4 flex-row items-center justify-between">
          <button
            type="button"
            onClick={() =>
              setSelectedValues(
                Object.fromEntries(filters.map((filter) => [filter.id, ""])),
              )
            }
            className=" rounded-[8px] border px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:flex-none"
          >
            {t("Clear all")}
          </button>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-[8px] bg-primary-perso px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:flex-none"
            >
              {t("Appliquer les filtres")}
            </button>
        </div>
      </div>
    </div>
  );
}

export default FiltersModal;